"use client";

import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardBody, CircularProgress, Tooltip } from "@heroui/react";
import { useSession } from "next-auth/react";
import { apiFetch } from "@/lib/api";
import { IconTrophy, IconSparkles, IconBulb, IconUsers, IconBolt, IconMessageCircle2 } from "@tabler/icons-react";

interface Selo {
  cor_fim: string;
  cor_inicio: string;
  descricao: string;
  habilitado: boolean;
  icone: string;
  id_selo: string;
  ordem: number;
  slug: string;
  tipo: string;
  titulo: string;
}

const getIcon = (icone: string) => {
  switch (icone) {
    case "sparkles":
      return <IconSparkles size={18} />;
    case "lightbulb":
      return <IconBulb size={18} />;
    case "users":
      return <IconUsers size={18} />;
    case "bolt":
      return <IconBolt size={18} />;
    case "trophy":
      return <IconTrophy size={18} />;
    case "chat-bubble-left-right":
      return <IconMessageCircle2 size={18} />;
    case "handshake":
      return <IconMessageCircle2 size={18} />;
    default:
      return <IconTrophy size={18} />;
  }
};

export default function BadgesAndSigns() {
  const { data: session } = useSession();
  const [selos, setSelos] = useState<Selo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.accessToken) return;

    const fetchSelos = async () => {
      try {
        setLoading(true);

        const res = await apiFetch("/selos/?habilitado=true", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          setSelos([]);
          return;
        }

        const data = await res.json();
        // garante ordem consistente
        const ordered = (data as Selo[]).sort((a, b) => a.ordem - b.ordem);
        setSelos(ordered);
      } catch (error) {
        console.error("Erro ao carregar selos", error);
        setSelos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSelos();
  }, [session?.accessToken]);

  return (
    <Card className="w-full p-4 md:p-6">
      <CardHeader className="flex items-center gap-2">
        <div className="rounded-full bg-emerald-500/10 p-2 flex items-center justify-center">
          <IconTrophy size={22} className="text-emerald-500" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold">Badges &amp; Selos</h2>
      </CardHeader>

      <CardBody>
        {loading ? (
          <div className="w-full flex justify-center items-center py-4">
            <CircularProgress size="md" />
          </div>
        ) : selos.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum selo disponível no momento.</p>
        ) : (
          <div className="flex flex-wrap gap-3 mt-2">
            {selos.map((selo) => (
              <Tooltip key={selo.id_selo} content={selo.descricao} showArrow placement="bottom-start">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all"
                  style={{
                    background: `linear-gradient(90deg, ${selo.cor_inicio}, ${selo.cor_fim})`,
                  }}
                >
                  {getIcon(selo.icone)}
                  <span>{selo.titulo}</span>
                </button>
              </Tooltip>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
